<script setup>
import {onMounted} from "vue";
import {useHead} from "@vueuse/head";

onMounted(() => {
  const scripts = [
    '/js/core.min.js',
    '/js/script.js'
  ];

  function loadScriptsSequentially(scripts, index = 0) {
    if (index < scripts.length) {
      const script = document.createElement('script');
      script.src = scripts[index];
      script.onload = () => loadScriptsSequentially(scripts, index + 1);
      document.body.appendChild(script);
    }
  }

  loadScriptsSequentially(scripts);
})

useHead({
  title: 'Privacy Policy - TPA Housing',
  meta: [
    {
      name: 'description',
      content: 'Privacy Policy for TPA Housing - Learn how we collect, use, and protect your personal information.'
    }
  ]
})
</script>

<template>
  <div class="page">
    <section class="section novi-background context-dark">
      <div class="container">
        <header class="page-header" style="padding: 20px 0;">
          <a href="/">
            <img src="/images/tpa3.png" alt="TPA Housing" width="223" height="60"/>
          </a>
        </header>

        <div class="privacy-policy-content" style="max-width: 800px; margin: 0 auto; padding: 40px 20px;">
          <h1 style="font-size: 36px; margin-bottom: 30px; color: #333;">Privacy Policy</h1>
          <p style="color: #666; margin-bottom: 40px; font-size: 16px; line-height: 1.6;">Last Updated: December
            2024</p>

          <div class="policy-sections">
            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">1. Introduction</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">
                TPA Housing ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains
                how we collect, use, disclose, and safeguard your information when you visit our website.
              </p>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">2. Information We Collect</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">We may collect the following types of
                information:</p>
              <ul style="padding-left: 20px; margin-bottom: 15px; color: #555;">
                <li style="margin-bottom: 10px; line-height: 1.6;"><strong>Personal Information:</strong> Name, email
                  address, phone number, company details
                </li>
                <li style="margin-bottom: 10px; line-height: 1.6;"><strong>Contact Information:</strong> Address,
                  country, city
                </li>
                <li style="margin-bottom: 10px; line-height: 1.6;"><strong>Technical Data:</strong> IP address, browser
                  type, operating system
                </li>
                <li style="margin-bottom: 10px; line-height: 1.6;"><strong>Usage Data:</strong> Pages visited, time
                  spent on site, referral sources
                </li>
              </ul>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">3. How We Use Your Information</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">We use the collected information for:</p>
              <ul style="padding-left: 20px; margin-bottom: 15px; color: #555;">
                <li style="margin-bottom: 10px; line-height: 1.6;">Providing and maintaining our services</li>
                <li style="margin-bottom: 10px; line-height: 1.6;">Responding to inquiries and providing support</li>
                <li style="margin-bottom: 10px; line-height: 1.6;">Improving our website and services</li>
                <li style="margin-bottom: 10px; line-height: 1.6;">Communicating updates and marketing materials (with
                  consent)
                </li>
                <li style="margin-bottom: 10px; line-height: 1.6;">Complying with legal obligations</li>
              </ul>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">4. Data Security</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">
                We implement appropriate technical and organizational security measures to protect your personal
                information against unauthorized access, alteration, disclosure, or destruction.
              </p>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">5. Third-Party Services</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">
                We may employ third-party companies and individuals to facilitate our services, provide services on our
                behalf, or assist in analyzing how our services are used. These third parties have access to your
                personal information only to perform these tasks and are obligated not to disclose or use it for any
                other purpose.
              </p>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">6. Cookies and Tracking Technologies</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">
                We use cookies and similar tracking technologies to track activity on our website and hold certain
                information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being
                sent.
              </p>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">7. Your Rights</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">Depending on your location, you may have
                the following rights:</p>
              <ul style="padding-left: 20px; margin-bottom: 15px; color: #555;">
                <li style="margin-bottom: 10px; line-height: 1.6;">Access to your personal information</li>
                <li style="margin-bottom: 10px; line-height: 1.6;">Correction of inaccurate data</li>
                <li style="margin-bottom: 10px; line-height: 1.6;">Deletion of your personal information</li>
                <li style="margin-bottom: 10px; line-height: 1.6;">Objection to processing of your data</li>
                <li style="margin-bottom: 10px; line-height: 1.6;">Data portability</li>
              </ul>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">8. Children's Privacy</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal
                information from children under 18.
              </p>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">9. Changes to This Privacy Policy</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new
                Privacy Policy on this page and updating the "Last Updated" date.
              </p>
            </div>

            <div class="section" style="margin-bottom: 40px;">
              <h2 style="font-size: 24px; margin-bottom: 20px; color: #333;">10. Contact Us</h2>
              <p style="margin-bottom: 15px; line-height: 1.6; color: #555;">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <ul style="padding-left: 20px; margin-bottom: 15px; color: #555;">
                <li style="margin-bottom: 10px; line-height: 1.6;"><strong>Email:</strong> privacy@tpahousing.com</li>
                <li style="margin-bottom: 10px; line-height: 1.6;"><strong>Address:</strong> TPA Housing, Industrial
                  Zone, Shanghai, China
                </li>
              </ul>
            </div>
          </div>

          <div class="back-home" style="margin-top: 50px; text-align: center;">
            <a href="/" class="button button-lg button-primary"
               style="display: inline-block; padding: 12px 30px; background: #007bff; color: white; text-decoration: none; border-radius: 4px; font-weight: 500;">
              Back to Home
            </a>
          </div>
        </div>

        <footer class="page-footer"
                style="margin-top: 60px; padding: 30px 0; border-top: 1px solid #eee; text-align: center;">
          <p class="rights" style="color: #666; font-size: 14px;">
            <span>&copy;&nbsp;</span>
            <span class="copyright-year">2024</span>
            <span>&nbsp;</span>
            <span>TPA Housing. All rights reserved.</span>
          </p>
        </footer>
      </div>
    </section>
  </div>
  <!-- Global Mailform Output-->
  <div class="snackbars" id="form-output-global"></div>
</template>

<style scoped lang="scss">
.page {
  background-color: #fff;
}

.section {
  padding: 40px 0;
  background-color: #fff;
}

.privacy-policy-content {
  h1 {
    font-weight: 600;
    color: #1a1a1a;
  }

  h2 {
    font-weight: 600;
    color: #1a1a1a;
  }

  p {
    color: #4a5568;
    line-height: 1.8;
  }

  ul {
    list-style-type: disc;
    margin-left: 20px;

    li {
      margin-bottom: 8px;
      line-height: 1.6;
    }
  }
}

.button-primary {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 4px;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #0056b3;
  }
}

@media (max-width: 768px) {
  .privacy-policy-content {
    padding: 20px 15px;

    h1 {
      font-size: 28px;
    }

    h2 {
      font-size: 20px;
    }
  }
}
</style>